import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { createFileRoute, Link, type ErrorComponentProps } from '@tanstack/react-router'
import { Award, Bell, Database, Info, Languages, MessageSquare, ShieldCheck } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import Avatar from '@/components/ui/Avatar'
import Button from '@/components/ui/Button'
import { buttonVariants } from '@/components/ui/button-variants'
import { NOT_AVAILABLE_TITLE } from '@/components/ui/empty-state-variants'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import {
  usePreferences,
  useSuspenseMemberProfile,
  useSuspenseMe,
} from '@/features/account/api/queries'
import { tierOf, upgradeFrom } from '@/features/account/plan'
import {
  planQueryOptions,
  plansQueryOptions,
  useSuspenseSubscriptionMe,
} from '@/features/subscriptions/api/queries'
import { buildCatalog, cheapestPlan } from '@/features/subscriptions/lib/catalog'
import { planSummary } from '@/features/subscriptions/lib/features'
import { formatBsDate } from '@/lib/bsDate'
import { useAuthStore } from '@/lib/auth/store'
import { formatNPR } from '@/lib/format'
import type { Money } from '@/lib/money'
import { formatPhone } from '@/lib/phone'
import { useUiStore } from '@/lib/ui-store'
import { cn } from '@/lib/utils'
import type { PlanDetail } from '@/schemas/subscriptions'
import LogOutDialog from '@/components/layout/LogOutDialog'
import SettingsGroup from '@/screens/account/components/SettingsGroup'
import SettingsRow from '@/screens/account/components/SettingsRow'
import { APP_VERSION } from '@/screens/account/components/app-meta'
import { loadProfile } from '@/screens/account/ProfileScreen.loader'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title="Account" />

const iconClass = 'size-5'

/**
 * Mock `ac-card` (COMPARE-ac §1): the surface fill with no edge and no radius. The identity card
 * pads 20 24, the upsell 16 20 16 24 (the mock's 17/21/25 on the grid).
 */
const IDENTITY_CARD = 'flex flex-wrap items-center gap-x-5 gap-y-4 bg-card px-6 py-5'
const UPSELL_CARD = 'flex flex-wrap items-center gap-x-5 gap-y-3 bg-card py-4 pr-5 pl-6'

/**
 * The hub's four groups (mock `ac-hub`): Plan; Notifications and Appearance; Data and privacy and
 * Sign in and devices; Help and About. One list for the loaded page and its skeleton, so the two
 * cannot drift; only the values differ.
 */
function HubGroups({
  plan,
  notifications,
  appearance,
}: {
  plan: ReactNode
  notifications: ReactNode
  appearance: ReactNode
}) {
  return (
    <>
      <SettingsGroup label="Your account" inset="hub">
        <SettingsRow
          icon={<Award className={iconClass} aria-hidden="true" />}
          title="Plan"
          value={plan}
          link={{ kind: 'route', to: '/subscription' }}
        />
      </SettingsGroup>

      <SettingsGroup label="Preferences" inset="hub">
        <SettingsRow
          icon={<Bell className={iconClass} aria-hidden="true" />}
          title="Notifications"
          value={notifications}
          link={{ kind: 'settings', tab: 'notifications' }}
        />
        <SettingsRow
          icon={<Languages className={iconClass} aria-hidden="true" />}
          title="Appearance and language"
          value={appearance}
          link={{ kind: 'settings', tab: 'appearance' }}
        />
      </SettingsGroup>

      <SettingsGroup label="Privacy" inset="hub">
        <SettingsRow
          icon={<Database className={iconClass} aria-hidden="true" />}
          title="Data and privacy"
          link={{ kind: 'settings', tab: 'privacy' }}
        />
        <SettingsRow
          icon={<ShieldCheck className={iconClass} aria-hidden="true" />}
          title="Sign in and devices"
          link={{ kind: 'settings', tab: 'security' }}
        />
      </SettingsGroup>

      <SettingsGroup label="Help" inset="hub">
        <SettingsRow
          icon={<MessageSquare className={iconClass} aria-hidden="true" />}
          title="Help and support"
          link={{ kind: 'settings', tab: 'help' }}
        />
        <SettingsRow
          icon={<Info className={iconClass} aria-hidden="true" />}
          title="About MoneyMitra"
          value={APP_VERSION}
          link={{ kind: 'settings', tab: 'help' }}
        />
      </SettingsGroup>
    </>
  )
}

/**
 * The hub before `me`/`profile` have loaded — the same cards and groups as the
 * loaded screen with a bar standing in for each value: every link here is a
 * fixed route or `?tab=`, so the layout is already known and every row stays
 * navigable while its value is still out (the `Line` idiom, docs/route-loading.md).
 *
 * Heights follow the loaded page at 1440 (mock `?state=loading`): the identity
 * card is 72 avatar + 40 padding = 112 (name 28 + contact 20 + tag 20 with 4/8
 * between = 80, under the avatar); the upsell is 24 + 20 + 32 padding = 76
 * against the 36 button; "Log out" is the `Button`'s own `h-9`.
 */
function AccountPending() {
  const loadingValue = <Skeleton className="h-4 w-16 rounded-xs" />
  return (
    <ScreenLayout pending pendingLabel="Loading your account…" header={HEADER} fullWidth>
      <section aria-label="Your details" className={IDENTITY_CARD}>
        <Skeleton className="size-18 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1">
          <div className="flex h-7 items-center">
            <Skeleton className="h-5 w-[44%] rounded-sm" />
          </div>
          <div className="mt-1 flex h-5 items-center">
            <Skeleton className="h-4 w-[30%] rounded-xs" />
          </div>
          <Skeleton className="mt-2 h-5 w-30 rounded-xs" />
        </div>
      </section>

      <section aria-hidden="true" className={UPSELL_CARD}>
        <div className="min-w-0 flex-1">
          <div className="flex h-6 items-center">
            <Skeleton className="h-4 w-48 rounded-xs" />
          </div>
          <div className="flex h-5 items-center">
            <Skeleton className="h-4 w-80 rounded-xs" />
          </div>
        </div>
        <Skeleton className="h-9 w-24 rounded-md" />
      </section>

      <HubGroups plan={loadingValue} notifications={loadingValue} appearance={loadingValue} />

      <Skeleton aria-hidden="true" className="h-9 w-24 rounded-md" />
    </ScreenLayout>
  )
}

/** The copy the page used for a first-load failure before it had a loader. */
function AccountError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Could not load your account"
      description="Check your connection and try again. Your settings are safe."
    />
  )
}

export const Route = createFileRoute('/_app/profile')({
  loader: loadProfile,
  pendingComponent: AccountPending,
  errorComponent: AccountError,
  component: AccountScreen,
})

/**
 * The upsell banner's line, after V2's "Price alerts, screener and baskets.
 * From Rs 208 a month, paid yearly": the tagline of the cheapest Sadasya plan
 * and that same plan's per-month price, so the banner never pairs a price
 * with features the plan it quotes does not grant (dev's Rs 99 Sadasya sends
 * no flags, and then only the price is said). Dev sells no Sadasya yearly,
 * so the price is `cheapestPlan`'s lowest per-month figure across the periods
 * that DO exist. Both reads are secondary (the loader only starts the
 * catalogue's): the line is empty, one line tall, until they answer — never
 * a guess meanwhile.
 *
 * One line, not a table keyed by tier: `upgradeFrom` only ever offers Sadasya
 * now (D30 — no Sadasya→Guru nudge).
 */
function upsellLine(
  cheapest: { plan: { id: number }; perMonth: Money } | null,
  detail: UseQueryResult<PlanDetail | null>,
): string | null {
  if (cheapest === null || detail.isPending) return null
  const price = `From ${formatNPR(cheapest.perMonth, { whole: true })} a month.`
  const tagline = detail.data ? planSummary(detail.data.features) : null
  return tagline === null ? price : `${tagline} ${price}`
}

/**
 * The Account hub — V2 node `4236:71539`, with the two tier variants
 * `4236:75370` (Sadasya) and `4236:77013` (Guru).
 *
 * V1 called this "Profile" and drew a two-column dashboard: an avatar block, a
 * plan card, an "At a glance" card of counts, and a column of link rows beside
 * them. V2 replaces all of it with a settings index — one profile card, an
 * upsell, then four labelled groups of rows — and moves the counts off the
 * screen entirely. The route keeps its `/profile` path because the top nav's
 * avatar links to it and this family does not own the route manifest.
 *
 * ## What is real here and what is not
 *
 * Real: the name, phone and email (all from `GET /api/v5/auth/me/`), the plan
 * and — for a paid member — its "Active until" date (`GET
 * subscriptions/me/`'s `activeUntil`, already null for Free — rule 2,
 * `schemas/subscriptions.ts`), the notification summary (the member's own
 * persisted settings), the theme and the log-out.
 *
 * Not on the wire, and therefore not drawn: **Member since**. V2 prints
 * "Member since 2 Shrawan 2083" under the name on all three tier frames, and
 * mm-be publishes no join date on the user payload — there is no endpoint to
 * ask. A date is the kind of thing a member would take at face value, so the
 * line is absent rather than filled with an em dash or a plausible number.
 *
 * The Plan row says "Active until", never "renews" — CONTRACT §9.7: nothing
 * auto-renews, and Free's own `expires_on` is a ~10-year date that would read
 * as a real expiry if shown at all (`activeUntil` is null for Free for
 * exactly this reason, so the row falls back to the bare tier word).
 *
 * `me`/`profile`/`subscriptionMe` are the route's MAIN data
 * (`ProfileScreen.loader.ts`), all three awaited, so all three are always
 * defined here — a first-load failure is the route's `AccountError` above,
 * and a background refetch failure (`me`/`profile` both `staleTime: Infinity`,
 * `subscriptionMe` 30s, so this is rare) keeps whatever last loaded rather
 * than blanking a working page, the same guarantee every suspense-query
 * screen in this wave gets for free.
 */
function AccountScreen() {
  const { data: me } = useSuspenseMe()
  const { data: profile } = useSuspenseMemberProfile()
  const { data: entitlement } = useSuspenseSubscriptionMe()
  const logOut = useAuthStore((state) => state.logOut)
  const theme = useUiStore((state) => state.theme)
  const [logOutOpen, setLogOutOpen] = useState(false)

  const tier = tierOf(me.plan)
  const upgrade = upgradeFrom(me.plan)
  const preferences = usePreferences()
  const contact = [
    profile.phone === null ? null : formatPhone(profile.phone),
    profile.email,
  ].filter((v) => v !== null)
  const themeWord = theme === 'system' ? 'System' : theme === 'dark' ? 'Dark' : 'Light'
  // English until the served preference says otherwise: it is what the app is in either way.
  const languageWord = preferences.data?.language === 'ne' ? 'नेपाली' : 'English'
  // Null for Free (rule 2) — the row then shows the bare tier word, never a
  // fake "Active until" for a plan nobody paid for.
  const planValue =
    entitlement.activeUntil === null
      ? tier
      : `${tier}, active until ${formatBsDate(entitlement.activeUntil)}`

  // Only a Free member's banner reads this, so a paid member's visit never
  // asks for the catalogue at all.
  const plans = useQuery({ ...plansQueryOptions(), enabled: upgrade !== null })
  const cheapestSadasya =
    plans.data === undefined ? null : cheapestPlan(buildCatalog(plans.data), 'bull')
  const cheapestDetail = useQuery({
    ...planQueryOptions(cheapestSadasya?.plan.id ?? 0),
    enabled: cheapestSadasya !== null,
  })

  return (
    <ScreenLayout header={HEADER} fullWidth>
      {/* Below `sm` the card is avatar | copy with Edit under the copy, as the mock stacks it,
          instead of the name squeezed into what is left beside the button. */}
      <section
        aria-label="Your details"
        className={cn(IDENTITY_CARD, 'max-sm:grid max-sm:grid-cols-[auto_1fr]')}
      >
        <Avatar
          {...(me.avatar === null ? {} : { src: me.avatar })}
          name={profile.fullName}
          size="lg"
          // Mock 72 (`ac-id__av`); initials until mm-be stores a photo (AC2).
          className="size-18 text-h-section"
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-h-section">{profile.fullName}</h2>
          {contact.length > 0 && (
            <p className="mt-1 flex flex-wrap items-center gap-x-2 text-body-m text-muted-foreground">
              {contact.map((line, index) => (
                <span key={line} className="flex items-center gap-x-2">
                  {index > 0 && <span aria-hidden="true">·</span>}
                  {line}
                </span>
              ))}
            </p>
          )}
          {/* The mock follows the tag with "Member since 2 Shrawan 2083"; mm-be sends no join
              date (asks AC1), so the line is not drawn. */}
          <p className="mt-2">
            <span className="inline-flex h-5 items-center bg-muted px-2 text-body-xs text-muted-foreground">
              {tier} plan
            </span>
          </p>
        </div>
        <Link
          to="/settings"
          search={{ tab: 'edit-profile' }}
          className={cn(
            buttonVariants({ variant: 'secondary' }),
            'max-sm:col-start-2 max-sm:justify-self-start',
          )}
        >
          Edit profile
        </Link>
      </section>

      {upgrade === null ? null : (
        <section aria-label={`Upgrade to ${upgrade}`} className={UPSELL_CARD}>
          <div className="min-w-0 flex-1">
            <h2 className="text-h-m">Get more with {upgrade}</h2>
            <p className="min-h-lh text-body-m text-muted-foreground-subtle">
              {upsellLine(cheapestSadasya, cheapestDetail)}
            </p>
          </div>
          <Link to="/subscription" className={buttonVariants({ variant: 'primary' })}>
            See plans
          </Link>
        </section>
      )}

      <HubGroups
        plan={planValue}
        // No notification-preferences endpoint (AC5): nothing to summarise.
        notifications={NOT_AVAILABLE_TITLE}
        appearance={`${themeWord}, ${languageWord}`}
      />

      <div>
        <Button
          variant="secondary"
          // Opens the confirm below rather than signing out on the spot — a
          // misclick or a stray Enter on a focused button used to end the
          // session with no way back short of signing in again.
          onClick={() => {
            setLogOutOpen(true)
          }}
        >
          Log out
        </Button>
      </div>

      <LogOutDialog
        open={logOutOpen}
        onOpenChange={setLogOutOpen}
        onConfirm={() => {
          void logOut()
        }}
      />
    </ScreenLayout>
  )
}
